import React, { useState, useEffect, useRef } from 'react'
import Modal from '@mui/material/Modal'
import { Box, DialogActions, DialogContent, useMediaQuery, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Typography from '@mui/material/Typography'
import { useForm } from 'react-hook-form'
import { Input } from 'Components/Input'
import Button from '@mui/material/Button'
import CustomButton from 'Components/CustomButton/CustomButton'
import TaskPng from 'Components/images/Task Icon big.png'
import { makePostRequest, makeGetRequest } from 'utils/api'
import urls from 'utils/urls'
import CreateTache from './CreateTache'
import ComboBox from 'Components/ComboBox'

import CircularProgress from '@mui/material/CircularProgress'
import OpenAiTask from './OpenAiTask'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const mstyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  // width: 500,  borderRadius: 2,
  borderRadius: 2,

  bgcolor: 'background.paper',
  boxShadow: 24,
}
export default function CreateProject({
  setCreateProject,
  updateTodo = false,
  setProjectList,
  setProjectRow,
  // LAP-fix-projet-vs-objectif — prefill optionnel passé par MesToDo quand on
  // arrive depuis le Conseiller Patrimonial (route /todos?action=new-project).
  // Contient { Name, Objectifs, DateFinObjectif, LifeDomain, autofilled? }.
  prefill = null,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [loading, setLoading] = useState(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [modelOpen, setModelOpen] = useState(true)
  const [createTache, setCreateTache] = useState(false)
  const [tachePayload, setTachePayload] = useState(false)
  const [domain, setDomain] = useState([])
  // LAP-fix-projet-vs-objectif — Valeur contrôlée de la ComboBox Domaine, pour
  // pouvoir pré-sélectionner "Finance" quand on arrive depuis le Conseiller
  // Patrimonial (prefill.LifeDomain = 'Finance', une string à matcher contre la
  // liste des domaines chargée par fetchDomain).
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [showTaskModel, setShowTaskModel] = useState(false)
  const [tasklist, setTasklist] = useState([])
  // LAP refonte Mes Objectifs — champ « Objectif lié » : objectifs actifs
  // (mesobjectifs-linkables) ; à la création du projet, on pose la liaison
  // ObjectiveLink via POST /mesobjectifs/<id>/links/ (sens projet → objectif).
  const [objectivesOptions, setObjectivesOptions] = useState([])
  const [linkedObjective, setLinkedObjective] = useState(null)

  const Name_ref = useRef()
  const Objectifs_ref = useRef()
  const DateFinObjectif_ref = useRef()
  const LifeDomain_ref = useRef()

  const CreateTacheModal = () => {
    setCreateTache(true)
  }
  const handleModelClose = () => {
    setModelOpen(false)
    setCreateProject(false)
    reset()
  }

  const { handleSubmit, control, reset, setValue, getValues } = useForm({
    defaultValues: {
      Name: prefill?.Name || '',
      Objectifs: prefill?.Objectifs || '',
      DateFinObjectif: prefill?.DateFinObjectif || '',
      // LifeDomain reste vide au départ : la valeur réelle (objet {label,id})
      // est résolue par fetchDomain via le matching du prefill.LifeDomain.
      LifeDomain: '',
    },
  })
  useEffect(() => {
    fetchDomain()
    fetchLinkableObjectives()
  }, [])

  // LAP refonte Mes Objectifs — objectifs actifs pour le champ « Objectif lié ».
  const fetchLinkableObjectives = async () => {
    const { data, status } = await makeGetRequest(urls.mesObjectifsLinkables, {}, true)
    if (status === 200) {
      setObjectivesOptions(
        (data.objectives || []).map((o) => ({ label: o.label, id: o.id })),
      )
    }
  }

  // LAP-fix-projet-vs-objectif — Re-applique le prefill quand il arrive après
  // le mount (cas où useForm a déjà initialisé avec des valeurs vides).
  useEffect(() => {
    if (!prefill) return
    if (prefill.Name) setValue('Name', prefill.Name)
    if (prefill.Objectifs) setValue('Objectifs', prefill.Objectifs)
    if (prefill.DateFinObjectif) setValue('DateFinObjectif', prefill.DateFinObjectif)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill])

  const fetchDomain = async () => {
    const url = urls.getDomain + '?limit=100000'
    const { data, status } = await makeGetRequest(url, {}, true)
    if (status === 200) {
      const filterTypes = []
      data.forEach((v) => {
        filterTypes.push({ label: `${v.domain}`, id: v.id })
      })
      setDomain([...filterTypes])

      // LAP-fix-projet-vs-objectif — Si le formulaire est ouvert avec un
      // prefill.LifeDomain (ex. 'Finance' depuis le Conseiller Patrimonial),
      // on cherche l'option correspondante (insensible à la casse) et on la
      // pré-sélectionne dans la ComboBox + dans le state du formulaire.
      const wanted = prefill?.LifeDomain
      if (wanted) {
        const wantedLabel =
          typeof wanted === 'string' ? wanted : wanted.label || ''
        const wantedId = typeof wanted === 'object' ? wanted.id : undefined
        const match = filterTypes.find((o) =>
          wantedId !== undefined
            ? o.id === wantedId
            : o.label.toLowerCase() === String(wantedLabel).toLowerCase(),
        )
        if (match) {
          setSelectedDomain(match)
          setValue('LifeDomain', match)
        }
      }
    }
  }
  const onSubmit = async (data) => {
    let payload = {}
    payload = {
      ...data,
      LifeDomain: data.LifeDomain.id,
    }
    const response = await makePostRequest(urls.projectOperations, payload, {}, true)
    if (response.status === 201) {
      let projectData = response.data
      projectData = {
        ...projectData,
        tasks: [],
      }
      let pId = projectData.id
      // LAP refonte Mes Objectifs — liaison « Objectif lié » posée dès la
      // création (sens inverse : le projet s'attache à l'objectif choisi).
      if (linkedObjective) {
        await makePostRequest(
          urls.mesObjectifsLinks(linkedObjective.id),
          { add: [{ target_type: 'project', target_id: String(pId) }] },
          {},
          true,
        )
      }
      if (tachePayload) {
        let payload = {}
        payload = {
          ...tachePayload,
          ProjectId: pId,
        }
        const response = await makePostRequest(urls.taskOperations, payload, {}, true)
        if (response.status === 201) {
          projectData.tasks = [response.data]
        }
      }
      if (tasklist.length > 0) {
        // forEach(async) n'attendait pas les POST : le projet s'affichait
        // sans ses tâches et les erreurs d'enregistrement étaient avalées.
        const responses = await Promise.all(
          tasklist.map((el) =>
            makePostRequest(
              urls.taskOperations,
              {
                Nom: el.Name,
                Descriptions: el.Description,
                DateLimite: el.Date_limite,
                Priorite: el.Urgence,
                Categorie: el.cat_id,
                Couts: el.Cost_estime,
                ProjectId: pId,
              },
              {},
              true,
            ),
          ),
        )
        const rssTaskList = responses
          .filter((r) => r.status === 201)
          .map((r) => r.data)
        const failed = tasklist.length - rssTaskList.length
        if (failed > 0) {
          alert(t('mesToDo.taskSaveError', { failed, total: tasklist.length }))
        }
        projectData.tasks = rssTaskList
      }

      setProjectList((notesList) => [...notesList, projectData])
      handleModelClose()
      if (updateTodo) {
        updateTodo()
      }
    }
  }
  const handleOpenAi = async () => {
    const nameInput = getValues('Name')
    if (!nameInput || !nameInput.trim()) {
      Name_ref.current.focus()
      return
    }

    const objectifInput = getValues('Objectifs')
    if (!objectifInput || !objectifInput.trim()) {
      Objectifs_ref.current.focus()
      return
    }

    const dateInput = getValues('DateFinObjectif')
    if (!dateInput || !dateInput.trim()) {
      DateFinObjectif_ref.current.focus()
      return
    }

    const lifeDomainInput = getValues('LifeDomain')
    if (!lifeDomainInput) {
      LifeDomain_ref.current.focus()
      return
    }

    // return
    setLoading(true)
    const payload = {
      Name: getValues('Name'),
      Objectifs: getValues('Objectifs'),
      DateFinObjectif: getValues('DateFinObjectif'),
      LifeDomain: getValues('LifeDomain').label,
    }
    const response = await makePostRequest('/api/mestodo/genrate-openai-detail/', payload, {}, true)
    if (response.status === 200) {
      setLoading(false)
      setShowTaskModel(true)
      setTasklist(response.data)
    } else {
      setTasklist([])
      setLoading(false)
      alert(t('mesToDo.openAiError'))
    }
  }
  // console.log(tasklist)
  return (
    <>
      {showTaskModel && (
        <OpenAiTask
          setShowTaskModel={setShowTaskModel}
          tasklist={tasklist}
          setTasklist={setTasklist}
        />
      )}
      <Modal
        open={modelOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ '.projadd': { width: isMobile ? '90%' : '500px' } }}
      >
        <Box sx={mstyle} className="projadd">
          <Box>
            <Box
              sx={{ textAlign: 'end', padding: '5px 10px', position: 'relative', zIndex: '999' }}
            >
              <CloseIcon sx={{ cursor: 'pointer' }} onClick={handleModelClose} />
            </Box>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '-30px',
                }}
              >
                <Box sx={{ width: '-webkit-fill-available' }}>
                  <Typography
                    id="modal-modal-title"
                    sx={{ color: 'black !important' }}
                    variant="h6"
                    component="h3"
                  >
                    {t('mesToDo.newProject')}
                  </Typography>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                      <Input
                        inputRef={Name_ref}
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          required: true,
                          size: 'small',
                          type: 'text',
                        }}
                        rules={{ required: true }}
                        name="Name"
                        label="Nom du projet"
                      />
                      <Input
                        inputRef={Objectifs_ref}
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          required: true,
                          size: 'small',
                          type: 'text',
                        }}
                        rows={4}
                        multiline
                        rules={{ required: true }}
                        name="Objectifs"
                        label="Objectif"
                      />
                      <Input
                        inputRef={DateFinObjectif_ref}
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          required: true,
                          size: 'small',
                          type: 'date',
                        }}
                        rules={{ required: true }}
                        name="DateFinObjectif"
                        label="Date de fin de l'objectif"
                        InputLabelProps={{ shrink: true }}
                      />
                      <ComboBox
                        inputRef={LifeDomain_ref}
                        handleAutoComplete={(e, j) => {
                          setValue('LifeDomain', j)
                          setSelectedDomain(j)
                        }}
                        selectedValue={selectedDomain}
                        label={t('mesToDo.fieldDomain')}
                        data={domain}
                        name="LifeDomain"
                        required={true}
                      />
                      {/* LAP refonte Mes Objectifs — champ « Objectif lié » (optionnel) */}
                      <ComboBox
                        handleAutoComplete={(e, j) => setLinkedObjective(j)}
                        selectedValue={linkedObjective}
                        label={t('mesObjectifs.linkedObjectiveLabel')}
                        data={objectivesOptions}
                        name="linkedObjective"
                      />
                    </DialogContent>
                    <DialogActions>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '0',
                          '& Button': { minWidth: '0', color: '#434343', padding: '0' },
                        }}
                      >
                        <CustomButton
                          onClick={() => {
                            CreateTacheModal()
                          }}
                        >
                          <img alt="" src={TaskPng} />
                        </CustomButton>
                        <Button
                          sx={{ ml: 2 }}
                          title={t('mesToDo.openAiTitle')}
                          onClick={handleOpenAi}
                          loading={loading}
                          disabled={loading}
                          loadingPosition="start"
                        >
                          {loading ? (
                            <CircularProgress />
                          ) : (
                            <img src="/openai-svg.svg" style={{ height: 30 }} alt="oai" />
                          )}
                        </Button>
                      </Box>

                      <Button sx={{ marginLeft: 'auto !important' }} type="submit">
                        {t('mesToDo.validate')}
                      </Button>
                    </DialogActions>
                  </form>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
      {createTache && (
        <CreateTache
          setCreateTache={setCreateTache}
          setTachePayload={setTachePayload}
          setpayloadcheck={true}
        />
      )}
    </>
  )
}
